import { HeartHandshake, Wallet, Shield, Sparkles, Handshake, BadgeCheck, MessagesSquare, Users } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  useSeoMeta, LandingHero, CoverageGrid, WhyUsSection,
  TestimonialsSection, FaqSection, Breadcrumb,
} from "../lib/landingShared";

const HERO_IMG = "https://images.pexels.com/photos/3818963/pexels-photo-3818963.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=700";

const BENEFITS = [
  { icon: HeartHandshake, title: "Protezione della famiglia", desc: "Un capitale per chi ti è vicino, nei momenti più delicati." },
  { icon: Wallet,         title: "Capitale e previdenza",     desc: "Strumenti per integrare la pensione pubblica o accantonare nel tempo, con vantaggi fiscali." },
  { icon: Shield,         title: "Pianificazione di lungo periodo", desc: "Una polizza che si adatta nel tempo." },
  { icon: Sparkles,       title: "Soluzioni su misura",       desc: "Temporanea caso morte, multiramo, PIP o fondo pensione: scegliamo insieme cosa serve davvero." },
];

const WHY = [
  { icon: Handshake,      title: "Una conversazione, prima del prodotto", desc: "Famiglia, progetti, priorità: ascoltiamo prima di consigliare." },
  { icon: BadgeCheck,     title: "Chiarezza sulle clausole",              desc: "Costi, rendimenti attesi, vincoli e riscatti spiegati prima della firma." },
  { icon: MessagesSquare, title: "Consulenza nel tempo",                  desc: "Un referente che resta. Rivediamo insieme la polizza quando cambia qualcosa nella tua vita." },
  { icon: Users,          title: "Più compagnie a confronto",             desc: "Confrontiamo le proposte delle principali compagnie del settore." },
];

const FAQS = [
  { q: "A cosa serve una polizza vita?",
    a: "Serve a garantire un capitale o una rendita a sé stessi o ai propri cari, al verificarsi di un evento previsto (decesso, scadenza, invalidità). È uno strumento per proteggere la famiglia da imprevisti, integrare la pensione pubblica, accumulare nel tempo un capitale per progetti futuri o per i figli, ottenere agevolazioni fiscali sui versamenti." },
  { q: "Che differenza c'è tra TCM, previdenza e investimento?",
    a: "La Temporanea Caso Morte (TCM) è una polizza di pura protezione: garantisce un capitale ai beneficiari in caso di decesso dell'assicurato entro la durata del contratto. La previdenza (PIP, fondo pensione aperto) accumula capitale destinato a integrare la pensione. L'investimento assicurativo (Multiramo, ramo I, III) accumula capitale rivalutabile o legato a fondi, con la flessibilità di riscatto. In consulenza valutiamo insieme quale combinazione è più adatta." },
  { q: "Posso disdire o riscattare la polizza?",
    a: "Sì, le polizze vita prevedono normalmente la possibilità di riscatto totale o parziale dopo un certo periodo (in genere 1-2 anni). Le condizioni e gli eventuali costi di riscatto sono indicati chiaramente nel set informativo precontrattuale, che ti consegniamo prima della sottoscrizione." },
  { q: "Quali vantaggi fiscali offre?",
    a: "I premi versati su polizze TCM sono detraibili al 19% fino a 530 € l'anno; sui fondi pensione e PIP è prevista una deducibilità dal reddito fino a 5.164,57 € l'anno. Le prestazioni in caso di decesso sono esenti da imposta di successione. Le polizze vita beneficiano inoltre di un trattamento fiscale agevolato sui rendimenti. In consulenza analizziamo i benefici concreti sul tuo profilo." },
];

const TRUST = [
  { value: "1958", label: "Anno di fondazione" },
  { value: "3",    label: "Generazioni accanto alle famiglie" },
  { value: "24h",  label: "Risposta alla richiesta" },
];

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": FAQS.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })),
};

export default function Vita() {
  useSeoMeta({
    title: "Polizza Vita — Protezione famiglia e previdenza | Da Pra Assicurazioni",
    description: "Polizza vita per la protezione della famiglia: TCM, previdenza, investimento e soluzioni personalizzate. Consulenza umana e trasparente, agenzia assicurativa plurimandataria dal 1958.",
    keywords: "polizza vita, assicurazione vita, protezione famiglia, TCM, previdenza integrativa, fondo pensione",
    canonical: "https://dapraassicurazioni.it/polizza-vita",
    jsonLd: JSON_LD, jsonLdId: "ld-vita",
  });

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <Breadcrumb current={{ label: "Polizza Vita", href: "/polizza-vita" }} />

      <LandingHero
        eyebrow="Polizza Vita · Dal 1958"
        title={<>Proteggi il futuro <span className="italic">di chi ti è accanto.</span></>}
        description="Un gesto di cura per la tua famiglia: protezione, un capitale per i progetti dei figli, una pensione integrativa. La costruiamo insieme, partendo dalla tua storia."
        imageSrc={HERO_IMG}
        imageAlt="Polizza vita — Da Pra Assicurazioni"
        ctaPrimaryLabel="Parla con un consulente"
        ctaPrimaryHref="/preventivo?type=vita"
        trustItems={TRUST}
      />

      <CoverageGrid
        eyebrow="Cosa puoi costruire"
        heading="Una tranquillità che si costruisce nel tempo."
        items={BENEFITS}
        cols={2}
      />

      <WhyUsSection
        eyebrow="Il nostro approccio"
        heading="Una relazione, prima di una polizza."
        body={[
          "Le polizze vita parlano di temi importanti: famiglia, futuro, sicurezza economica.",
          "Per questo crediamo che la cosa più importante non sia il prodotto, ma la relazione con chi te lo propone. Ti dedichiamo tempo, ascoltiamo i tuoi obiettivi e solo dopo costruiamo insieme la soluzione.",
        ]}
        items={WHY}
      />

      <TestimonialsSection heading="Famiglie che si sono affidate a noi." />
      <FaqSection heading="Domande frequenti sulla polizza vita." faqs={FAQS} />

      <Footer />
    </div>
  );
}
